/**
 * ObstructionGeneralInfo
 *
 * "Overview" section of the obstruction details panel. Mirrors the
 * `TaskGeneralInfo` shape — a top "Basic info" grid (read-only badges,
 * dates, road) followed by read-only "Description" / "Internal notes"
 * sections. All fields are display-only; the single editable affordance is
 * the status field (`ObstructionStatusField` — state-machine-constrained,
 * gated by `readOnly`). Edits to other fields happen via the edit form.
 */
import { memo, type CSSProperties } from 'react';
import { Box, Group, Input, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendar,
  IconCalendarCheck,
  IconCalendarEvent,
  IconFlag,
  IconLayoutGrid,
  IconRoad,
  IconRouteAltLeft,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { ObstructionAffectedRoadsSection } from './parts/ObstructionAffectedRoadsSection';
import { ObstructionStatusField } from './parts/ObstructionStatusField';
import { SEVERITY_COLORS } from '@/constants/obstructions';
import { ICON_SIZE_TAB } from '@/constants/ui';
import type { Obstruction } from '@/types/obstructions';

import styles from './ObstructionGeneralInfo.module.css';

interface ObstructionGeneralInfoProps {
  obstruction: Obstruction;
  /** Display-only mode — gates the status field; empty optional sections hidden. */
  readOnly?: boolean;
}

export const ObstructionGeneralInfo = memo(function ObstructionGeneralInfo({
  obstruction,
  readOnly = false,
}: ObstructionGeneralInfoProps) {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  const severityStyle = {
    '--obstruction-severity-color': SEVERITY_COLORS[obstruction.severity],
  } as CSSProperties;

  return (
    <Stack gap="lg">
      {/* Title — read-only */}
      <Box className={styles.section}>
        <Group gap="xs" align="center">
          <Input.Label>{t('obstructions.detail.fields.title')}</Input.Label>
        </Group>
        <Text className={styles.titleValue}>{obstruction.title}</Text>
      </Box>

      {/* Basic info grid */}
      <Box className={styles.infoGrid}>
        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconLayoutGrid size={ICON_SIZE_TAB} />
            <Input.Label>{t('obstructions.detail.fields.type')}</Input.Label>
          </Group>
          <Text size="sm" className={styles.infoValue}>
            {t(`obstructions.type.${obstruction.type}`)}
          </Text>
        </Box>

        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconFlag size={ICON_SIZE_TAB} />
            <Input.Label>
              {t('obstructions.detail.fields.severity')}
            </Input.Label>
          </Group>
          <Text
            size="sm"
            className={styles.severityValue}
            style={severityStyle}
          >
            {t(`obstructions.severity.${obstruction.severity}`)}
          </Text>
        </Box>

        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconRouteAltLeft size={ICON_SIZE_TAB} />
            <Input.Label>{t('obstructions.detail.fields.impact')}</Input.Label>
          </Group>
          <Text size="sm" className={styles.infoValue}>
            {t(`obstructions.impact.${obstruction.impact}`)}
          </Text>
        </Box>

        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconAlertTriangle size={ICON_SIZE_TAB} />
            <Input.Label>{t('obstructions.detail.fields.status')}</Input.Label>
          </Group>
          <ObstructionStatusField
            obstructionId={obstruction.id}
            status={obstruction.status}
            readOnly={readOnly}
          />
        </Box>

        {obstruction.location !== null && obstruction.location.length > 0 && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconRoad size={ICON_SIZE_TAB} />
              <Input.Label>
                {t('obstructions.detail.fields.location')}
              </Input.Label>
            </Group>
            <Text size="sm" className={styles.infoValue}>
              {obstruction.location}
            </Text>
          </Box>
        )}

        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconCalendarEvent size={ICON_SIZE_TAB} />
            <Input.Label>
              {t('obstructions.detail.fields.plannedStart')}
            </Input.Label>
          </Group>
          <Text size="sm" className={styles.infoValue}>
            {formatDateCompact(obstruction.plannedStartDate)}
          </Text>
        </Box>

        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconCalendar size={ICON_SIZE_TAB} />
            <Input.Label>
              {t('obstructions.detail.fields.plannedEnd')}
            </Input.Label>
          </Group>
          <Text size="sm" className={styles.infoValue}>
            {formatDateCompact(obstruction.plannedEndDate)}
          </Text>
        </Box>

        {obstruction.actualStartDate !== null && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconCalendarEvent size={ICON_SIZE_TAB} />
              <Input.Label>
                {t('obstructions.detail.fields.actualStart')}
              </Input.Label>
            </Group>
            <Text size="sm" className={styles.infoValue}>
              {formatDateCompact(obstruction.actualStartDate)}
            </Text>
          </Box>
        )}

        {obstruction.actualEndDate !== null && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconCalendarCheck size={ICON_SIZE_TAB} />
              <Input.Label>
                {t('obstructions.detail.fields.actualEnd')}
              </Input.Label>
            </Group>
            <Text size="sm" className={styles.infoValue}>
              {formatDateCompact(obstruction.actualEndDate)}
            </Text>
          </Box>
        )}
      </Box>

      {/* Description — read-only */}
      {obstruction.description.trim().length > 0 && (
        <Box className={styles.descriptionItem}>
          <Input.Label>
            {t('obstructions.detail.fields.description')}
          </Input.Label>
          <Text className={styles.descriptionValue}>
            {obstruction.description}
          </Text>
        </Box>
      )}

      {/* Internal notes — read-only */}
      {obstruction.internalNotes !== null &&
        obstruction.internalNotes.length > 0 && (
          <Box className={styles.descriptionItem}>
            <Input.Label>
              {t('obstructions.detail.fields.internalNotes')}
            </Input.Label>
            <Text className={styles.descriptionValue}>
              {obstruction.internalNotes}
            </Text>
          </Box>
        )}

      {/* Affected roads — read-only */}
      {obstruction.affectedRoads.length > 0 && (
        <ObstructionAffectedRoadsSection
          affectedRoads={obstruction.affectedRoads}
        />
      )}
    </Stack>
  );
});
